"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addStateListener = exports.persistentStart = exports.persistentEnd = exports.addStatsListener = exports.disconnect = exports.connect = exports.mapPC = exports.mapTrack = exports.mapStream = exports.addEvent = exports.enableDataCollection = exports.disableDataCollection = exports.addKeys = exports.setUserRating = exports.addTags = exports.setConfig = exports.init = void 0;
require("./types");
var sdk_1 = require("./sdk");
var utils_1 = require("./utils");
// Centralized validation configuration
var VALIDATION_RULES = {
    rtcRoomId: { maxLength: 128 },
    rtcPeerId: { maxLength: 128 },
    keys: { maxLength: 32 },
    keysValue: { maxLength: 512 },
    comment: { maxLength: 2000 },
    event: { maxLength: 128 },
    name: { maxLength: 128 },
    displayName: { maxLength: 128 },
    roomId: { maxLength: 128 },
    peerId: { maxLength: 128 }
};
// Optimized helper function to validate a single property with sanitize
var validateProperty = function (value, rule, context) {
    var config = VALIDATION_RULES[rule];
    var propertyName = config.propertyName || rule;
    try {
        (0, utils_1.sanitize)(value, { propertyName: propertyName, maxLength: config.maxLength });
        return true;
    }
    catch (err) {
        var contextPrefix = context ? " in ".concat(context) : '';
        (0, sdk_1.sdkLog)("error", propertyName === "rtcRoomId" || propertyName === "rtcPeerId"
            ? "connection ignored - invalid ".concat(propertyName, " in watchRTCconfig ").concat(value)
            : "Input validation error: ".concat(propertyName).concat(contextPrefix, " value: ").concat(err.message), {
            propertyName: propertyName,
            value: value,
            error: err.message,
            context: context
        });
        return false;
    }
};
// Optimized helper function to validate keys object and return filtered valid keys
var validateKeys = function (keys) {
    var validKeys = {};
    var hasErrors = false;
    // Validate each key-value pair
    for (var _i = 0, _a = Object.entries(keys); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], value = _b[1];
        var isKeyValid = validateProperty(key, "keys");
        var isValueValid = typeof value === "string" ? validateProperty(value, "keysValue") : true;
        if (isKeyValid && isValueValid) {
            validKeys[key] = value; // Keep valid keys
        }
        else {
            hasErrors = true; // Mark that some keys were invalid
        }
    }
    return { isValid: !hasErrors, validKeys: validKeys };
};
// Unified validation function for watchRTC configuration
var validateWatchRTCConfig = function (config) {
    // Validate rtcRoomId
    if (config.rtcRoomId && !validateProperty(config.rtcRoomId, "rtcRoomId")) {
        return false;
    }
    // Validate rtcPeerId
    if (config.rtcPeerId && !validateProperty(config.rtcPeerId, "rtcPeerId")) {
        return false;
    }
    // Validate keys - if invalid, filter them but continue connection
    if (config.keys) {
        var keysValidation = validateKeys(config.keys);
        if (!keysValidation.isValid) {
            (0, sdk_1.sdkLog)("info", "Invalid keys detected - filtering out invalid keys but continuing connection");
            // Replace config.keys with only valid keys
            config.keys = keysValidation.validKeys;
        }
    }
    return true;
};
/**
 * Initialize SDK. Can be called multiple times but it will be initialized only at the first time.
 * Use 'setConfig' function after if you don't have rtcRoomId and rtcPeerId yet.
 * @param watchrtc
 * @param prefixesToWrap
 * @param services
 */
var init = function (watchrtcConfig, services) {
    if (!watchrtcConfig) {
        (0, sdk_1.initSDK)({}, [""], services);
        return;
    }
    if (validateWatchRTCConfig(watchrtcConfig)) {
        (0, sdk_1.initSDK)(watchrtcConfig, [""], services);
    }
};
exports.init = init;
/**
 * You also can use watchRTC.setConfig function to set watchRTC configuration after calling init()
 * and before the creation of RTCPeerConnection objects
 */
var setConfig = function (watchrtcConfig) {
    if (validateWatchRTCConfig(watchrtcConfig)) {
        (0, sdk_1.setConfig)(watchrtcConfig);
    }
};
exports.setConfig = setConfig;
/**
 * @deprecated Tags no longer supported. Use addKeys instead
 * Add tags for peer session
 * @param rtcTags[]
 */
var addTags = function (
/** ["tag1", "tag2", "tag3"] */
_rtcTags) { };
exports.addTags = addTags;
/**
 * Set user rating and/or comment for peer session
 * @param rating number from 1 to 5
 * @param comment string
 */
var setUserRating = function (
/** 1 | 2 | 3 | 4 | 5 */
rating, comment) {
    if (comment && !validateProperty(comment, "comment")) {
        (0, sdk_1.sdkLog)("info", "Invalid comment detected - ignoring comment but sending rating");
        comment = undefined; // Ignore the comment
    }
    (0, sdk_1.setUserRating)(rating, comment);
};
exports.setUserRating = setUserRating;
/**
 * Add keys for peer session
 * @param keys
 */
var addKeys = function (
/** { "key1": "value1", "key2": ["value2_1", "value2_2"]} */
keys) {
    var keysValidation = validateKeys(keys);
    if (!keysValidation.isValid) {
        (0, sdk_1.sdkLog)("info", "Invalid keys detected - filtering out invalid keys but continuing operation");
        keys = keysValidation.validKeys;
    }
    (0, sdk_1.addKeys)(keys);
};
exports.addKeys = addKeys;
/**
 * Disables data collection
 */
var disableDataCollection = function () {
    (0, sdk_1.disableDataCollection)();
};
exports.disableDataCollection = disableDataCollection;
/**
 * Enables data collection
 */
var enableDataCollection = function () {
    (0, sdk_1.enableDataCollection)();
};
exports.enableDataCollection = enableDataCollection;
var addEvent = function (event) {
    // Validate event name
    if (event.name && !validateProperty(event.name, "event", "addEvent")) {
        return;
    }
    // Validate and filter event parameters
    if (event.parameters && typeof event.parameters === "object") {
        var validParameters = {};
        var hasInvalidParams = false;
        for (var _i = 0, _a = Object.entries(event.parameters); _i < _a.length; _i++) {
            var _b = _a[_i], key = _b[0], value = _b[1];
            var isKeyValid = validateProperty(key, "keys");
            var isValueValid = typeof value === "string" ? validateProperty(value, "event") : true;
            if (isKeyValid && isValueValid) {
                validParameters[key] = value;
            }
            else {
                hasInvalidParams = true;
            }
        }
        if (hasInvalidParams) {
            (0, sdk_1.sdkLog)("info", "Invalid event parameters detected - filtering out invalid parameters but continuing operation");
            event.parameters = validParameters;
        }
    }
    (0, sdk_1.addEvent)(event);
};
exports.addEvent = addEvent;
/**
 * Map an incoming stream with a human-readable name
 * @param id The identifier of the stream (streamIdentifier attribute)
 * @param name The human-readable name
 * @deprecated
 */
var mapStream = function (id, name) {
    if (!validateProperty(id, "name", "mapStream")) {
        return;
    }
    if (!validateProperty(name, "name", "mapStream")) {
        return;
    }
    (0, sdk_1.mapStream)(id, name);
};
exports.mapStream = mapStream;
/**
 * Map an incoming track with a human-readable name
 * @param id The identifier of the track (trackIdentifier attribute)
 * @param name The human-readable name
 */
var mapTrack = function (id, name) {
    if (!validateProperty(id, "name", "mapTrack")) {
        return;
    }
    if (!validateProperty(name, "name", "mapTrack")) {
        return;
    }
    (0, sdk_1.mapTrack)(id, name);
};
exports.mapTrack = mapTrack;
/**
 * Map peer connection with a human-readable name
 * @param pc Peer connection object
 * @param displayName The human-readable name
 */
var mapPC = function (pc, displayName) {
    if (!validateProperty(displayName, "displayName", "mapPC")) {
        return;
    }
    (0, sdk_1.mapPC)(pc, displayName);
};
exports.mapPC = mapPC;
var connect = function () {
    (0, sdk_1.connect)();
};
exports.connect = connect;
var disconnect = function () {
    (0, sdk_1.disconnect)();
};
exports.disconnect = disconnect;
var addStatsListener = function (listener) {
    (0, sdk_1.registerOnStatsListener)(listener);
};
exports.addStatsListener = addStatsListener;
var persistentEnd = function (nailUpCallEnd) {
    (0, sdk_1.persistentEnd)(nailUpCallEnd);
};
exports.persistentEnd = persistentEnd;
var persistentStart = function (roomId, peerId) {
    if (!validateProperty(roomId, "roomId", "persistentStart")) {
        return;
    }
    if (!validateProperty(peerId, "peerId", "persistentStart")) {
        return;
    }
    (0, sdk_1.persistentStart)(roomId, peerId);
};
exports.persistentStart = persistentStart;
var addStateListener = function (listener) {
    (0, sdk_1.registerStateListener)(listener);
};
exports.addStateListener = addStateListener;
exports.default = {
    /**
     * Initialize SDK. Can be called multiple times but it will be initialized only at the first time.
     * Use 'setConfig' function after if you don't have rtcRoomId and rtcPeerId yet.
     * @param watchrtc
     * @param prefixesToWrap
     */
    init: exports.init,
    /**
     * @deprecated Tags no longer supported. Use addKeys instead
     * Add tags for peer session
     * @param rtcTags[]
     */
    addTags: exports.addTags,
    /**
     * Set user rating and/or comment for peer session
     * @param rating number from 1 to 5
     * @param comment string
     */
    setUserRating: exports.setUserRating,
    /**
     * Add keys for peer session
     * @param keys
     */
    addKeys: exports.addKeys,
    /**
     * You also can use watchRTC.setConfig function to set watchRTC configuration after calling init()
     * and before the creation of RTCPeerConnection objects
     */
    setConfig: exports.setConfig,
    /**
     * Disables data collection
     */
    disableDataCollection: exports.disableDataCollection,
    /**
     * Enables data collection
     */
    enableDataCollection: exports.enableDataCollection,
    addEvent: exports.addEvent,
    /**
     * Map an incoming stream with a human-readable name
     * @param id The identifier of the stream (streamIdentifier attribute)
     * @param name The human-readable name
     * @deprecated
     */
    mapStream: exports.mapStream,
    /**
     * Map an incoming track with a human-readable name
     * @param id The identifier of the track (trackIdentifier attribute)
     * @param name The human-readable name
     */
    mapTrack: exports.mapTrack,
    connect: exports.connect,
    disconnect: exports.disconnect,
    addStatsListener: exports.addStatsListener,
    persistentEnd: exports.persistentEnd,
    persistentStart: exports.persistentStart,
    addStateListener: exports.addStateListener,
    qualityrtc: {
        run: sdk_1.qrtcRun,
        stop: sdk_1.qrtcStop,
    },
    subscribe: sdk_1.subscribe,
    unsubscribe: sdk_1.unsubscribe,
};
